<template>
  <component :is="wrapComponent" class="ak-variables">
    <ak-loading
      v-model:active="isLoading"
      :can-cancel="false"
      :is-full-page="false"
    />

    <template v-if="!isLoading">
      <ak-tabs v-model="activeTab" justified>
        <ak-tab-item :label="leadLabel">
          <ak-variables-table
            :data="leadFields"
            :insert="addVariable"
            type="lead"
          />
        </ak-tab-item>

        <ak-tab-item :label="contactLabel">
          <ak-variables-table
            :data="contactFields"
            :insert="addVariable"
            type="contact"
          />
        </ak-tab-item>

        <ak-tab-item label="Account Variables">
          <ak-variables-table
            :data="accountVariables"
            :insert="addVariable"
            type="global"
          />
        </ak-tab-item>

        <ak-tab-item label="Survey Variables">
          <ak-variables-table
            :data="surveyFields"
            :insert="addVariable"
            type="survey"
          />
        </ak-tab-item>
      </ak-tabs>
    </template>
  </component>
</template>

<script>
import { defineComponent, ref, onMounted, computed } from 'vue'
import ModalBody from '../modal/ModalBody.vue'
import VariablesTable from './VariablesTable.vue'
import Loading from '../loading/Loading.vue'
import Tabs from '../tabs/Tabs.vue'
import TabItem from '../tabs/TabItem.vue'
import { get } from '../../utils/request.js'
import { isObject } from '../../utils/helpers'

import { entityBranding } from '../../services/entityBranding'

import {
  startTag,
  endTag,
  presetSurveyFields,
  presetContactFields,
  presetAccountVariables,
  presetLeadFields,
} from '../../utils/config'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-variables/>
   */
  name: 'AkVariables',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [VariablesTable.name]: VariablesTable,
    [Loading.name]: Loading,
    [TabItem.name]: TabItem,
    [Tabs.name]: Tabs,
    [ModalBody.name]: ModalBody,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Object}
     * @description The configuration for the close property, because
     * this is used in a modal window this will be the close method
     * for the modal instance.
     */
    close: {
      type: Function,
      default: () => {},
    },

    /**
     * @type {Object}
     * @description The configuration for the editor property, this
     * should be the CKEditor4 test editor instance, so that the
     * variables can be injected into the instance.
     */
    insert: {
      type: Function,
      default: () => {},
    },

    /**
     * @type {String}
     * @description The active account ID
     */
    account: {
      type: [String, Object],
      default: null,
    },

    /**
     * @type {String}
     * @description The active agency ID
     */
    agency: {
      type: [String, Object],
      default: null,
    },

    /**
     * @type Boolean
     * @description if true the outter most elemenet of this compnent will be
     * the ModalBody component.
     */
    modal: {
      type: Boolean,
      default: false,
    },
  },

  /**
   * @param {Object} props - The properties of the component
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props) {
    const isLoading = ref(true)
    const leadFields = ref([])
    const surveyFields = ref([])
    const contactFields = ref([])
    const accountVariables = ref([])
    const activeTab = ref(0)

    const wrapComponent = computed(() =>
      props.modal === true ? 'ak-modal-body' : 'div'
    )

    const accountId = computed(() =>
      isObject(props.account) ? props.account.id : props.account
    )

    const agencyId = computed(() =>
      isObject(props.agency) ? props.agency.id : props.agency
    )

    /**
     * @description Computed property for the account branded lead entity name
     */
    const leadLabel = computed(
      () => `${entityBranding('leads.singular', 'Lead')} Fields`
    )

    /**
     * @description Computed property for the account branded contact entity name
     */
    const contactLabel = computed(
      () => `${entityBranding('contacts.singular', 'Contact')} Fields`
    )

    /**
     * @param {String} type
     * @param {Object} variable
     * @description Tries to pass the variable tag through the callback
     * function.
     */
    const addVariable = (type, variable) => {
      props.insert(
        [
          startTag,
          type.toUpperCase(),
          ';',
          variable.name.toUpperCase(),
          endTag,
        ].join('')
      )
      props.close()
    }

    const getAccount = async () => {
      return await get(`/accounts/${accountId.value}`, {
        params: {
          agency: agencyId.value,
          overrideAccess: true,
        },
      })
    }

    const getFields = async () => {
      return await get('/custom_fields', {
        params: {
          account: accountId.value,
          agency: agencyId.value,
          per_page: 0,
          filters: '_id,tag,name,type',
          overrideAccess: true,
        },
      })
    }

    const mapContacts = ({ contacts }) => {
      return contacts.map(({ name, label }) => ({
        tag: label,
        name,
      }))
    }

    const mapVariables = (variables) => {
      return variables.map(({ name }) => ({
        tag: name,
        name,
      }))
    }

    const mapFields = (fields) => {
      return fields.map((item) => {
        return {
          tag: item.tag,
          name: item.name,
        }
      })
    }

    onMounted(async () => {
      const { data: fetchedAccount } = await getAccount()
      const { data } = fetchedAccount
      const { custom_data, variables } = data
      const { data: fetchedFields } = await getFields()
      const { data: fields } = fetchedFields

      const contacts = mapContacts(custom_data)
      const account = mapVariables(variables)
      const customFields = mapFields(fields)

      surveyFields.value = [...presetSurveyFields]
      leadFields.value = [...presetLeadFields, ...customFields]
      contactFields.value = [...presetContactFields, ...contacts]
      accountVariables.value = [...presetAccountVariables, ...account]
      isLoading.value = false
    })

    return {
      activeTab,
      isLoading,
      leadFields,
      surveyFields,
      contactFields,
      accountVariables,
      addVariable,
      leadLabel,
      contactLabel,
      wrapComponent,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';

.ak-variables {
  min-height: 200px;
  position: relative;

  .ak-tabs {
    &__content {
      max-height: 250px;
      overflow-y: scroll;
    }
  }
}
</style>
